import { useState, useEffect, useCallback } from "react";
import ShowcaseForm from "../components/showcase/ShowcaseForm";
import InstagramPostsDialog from "../components/showcase/InstagramPostsDialog";
import CommentsDialog from "../components/showcase/CommentsDialog";
import { getShowcases, deleteShowcase } from "../services/showcaseService";
import withUser from "@components/withUser";
import { getStylists, updateStylistDetails } from "@services/stylistService";
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Divider,
  Skeleton,
  Stack,
} from "@mui/material";
import NoStylist from "@components/NoStylist";
import { syncInstagramPosts, getInstagramPosts, initiateInstagramAuth, checkInstagramTokenStatus, disconnectInstagram } from "@services/instagramService";
import { isMobileDevice, openOAuthPopup, monitorPopup } from "../utils/mobileOAuthHandler";
import InstagramIcon from '@mui/icons-material/Instagram';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DisconnectIcon from '@mui/icons-material/LinkOff';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StylistSelector from "@components/StylistSelector";
import CommentIcon from '@mui/icons-material/Comment';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { PageContainer, PageHeader, SectionCard, EmptyState, LoadingState } from '../components/ui';
import { brand } from '../theme/tokens';

const ShowcaseManagementPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { stylistId } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [showcases, setShowcases] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [selectedStylist, setSelectedStylist] = useState(null);
  const [selectedShowcase, setSelectedShowcase] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showcaseToDelete, setShowcaseToDelete] = useState(null);
  const [isInstagramSyncing, setIsInstagramSyncing] = useState(false);
  const [showInstagramPosts, setShowInstagramPosts] = useState(false);
  const [instagramPosts, setInstagramPosts] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [isInstagramConnected, setIsInstagramConnected] = useState(false);
  const [instagramTokenStatus, setInstagramTokenStatus] = useState(null);
  const [isCheckingToken, setIsCheckingToken] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [isLoadingStylists, setIsLoadingStylists] = useState(false);
  const [isLoadingShowcases, setIsLoadingShowcases] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [isTogglingAutoSync, setIsTogglingAutoSync] = useState(false);

  const selectedStylistData = stylists.find(s => s._id === selectedStylist) || null;
  const autoContentSync = selectedStylistData?.autoContentSync;
  const hasInstagram = Boolean(selectedStylistData?.instagramUsername);

  const formatRelative = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const diffMs = d.getTime() - Date.now();
    const diffDays = Math.round(diffMs / (24 * 60 * 60 * 1000));
    if (diffDays === 0) return t('showcaseManagement.autoSyncToday', { defaultValue: 'today' });
    if (diffDays > 0) return t('showcaseManagement.autoSyncInDays', { count: diffDays, defaultValue: `in ${diffDays} days` });
    return t('showcaseManagement.autoSyncDaysAgo', { count: Math.abs(diffDays), defaultValue: `${Math.abs(diffDays)} days ago` });
  };

  const handleToggleAutoSync = async (event) => {
    if (!selectedStylist) return;
    const nextEnabled = event.target.checked;
    setIsTogglingAutoSync(true);
    try {
      await updateStylistDetails({ _id: selectedStylist, autoContentSync: JSON.stringify({ enabled: nextEnabled }) });
      setStylists(prev => prev.map(s => s._id === selectedStylist
        ? { ...s, autoContentSync: { ...(s.autoContentSync || {}), enabled: nextEnabled } }
        : s));
      toast.success(t(nextEnabled ? 'showcaseManagement.autoSyncEnabled' : 'showcaseManagement.autoSyncDisabled', {
        defaultValue: nextEnabled ? 'Auto content sync enabled' : 'Auto content sync disabled'
      }));
    } catch (error) {
      console.error('Error toggling auto content sync:', error);
      toast.error(t('showcaseManagement.autoSyncError', { defaultValue: 'Failed to update setting' }));
    } finally {
      setIsTogglingAutoSync(false);
    }
  };

  const handleCaptionChange = useCallback((postId, newCaption) => {
    setInstagramPosts(prevPosts =>
      prevPosts.map(post =>
        post.id === postId
          ? { ...post, caption: newCaption }
          : post
      )
    );
  }, []);

  const checkInstagramConnection = useCallback(async (stylistId) => {
    if (!stylistId) return;

    setIsCheckingToken(true);
    try {
      const tokenStatus = await checkInstagramTokenStatus(stylistId);
      setIsInstagramConnected(tokenStatus.hasToken);
      setInstagramTokenStatus(tokenStatus);

      // Show warning if expiring soon
      if (tokenStatus.isExpiringSoon && !tokenStatus.isExpired && tokenStatus.hasToken) {
        toast.warning(
          t('showcase.instagramExpiringSoon', {
            days: tokenStatus.expiresIn,
            defaultValue: `Instagram connection expires in ${tokenStatus.expiresIn} days. Please reconnect to avoid disruption.`
          })
        );
      }
    } catch (error) {
      console.error("Error checking Instagram connection:", error);
      setIsInstagramConnected(false);
      setInstagramTokenStatus(null);
    } finally {
      setIsCheckingToken(false);
    }
  }, [t]);

  useEffect(() => {
    if (selectedStylist) {
      checkInstagramConnection(selectedStylist);
    }
  }, [selectedStylist, checkInstagramConnection]);

  const fetchShowcases = useCallback(async () => {
    if (selectedStylist) {
      setIsLoadingShowcases(true);
      try {
        const data = await getShowcases(selectedStylist);
        setShowcases(data);
      } catch (error) {
        console.error("Error fetching showcases:", error);
      } finally {
        setIsLoadingShowcases(false);
      }
    }
  }, [selectedStylist]);

  useEffect(() => {
    fetchStylists();
  }, []);

  useEffect(() => {
    if (selectedStylist) {
      fetchShowcases();
    }
  }, [selectedStylist, fetchShowcases]);

  useEffect(() => {
    if (!showModal) {
      fetchShowcases();
    }
  }, [showModal]);

  const fetchStylists = async () => {
    setIsLoadingStylists(true);
    try {
      const data = await getStylists();
      setStylists(data);
      if (data.length > 0) {
        const stylistToSelect = stylistId || data[0]._id;
        setSelectedStylist(stylistToSelect);
      }
    } catch (error) {
      console.error("Error fetching stylists:", error);
    } finally {
      setIsLoadingStylists(false);
    }
  };

  const handleStylistSelect = (stylistId) => {
    setSelectedStylist(stylistId);
    navigate(`/showcase-management/${stylistId}`);
  };

  const handleInstagramAuth = async () => {
    if (!selectedStylist) return;

    setIsLoadingPosts(true);
    try {
      // For mobile popup flow, redirect to callback handler page
      const returnUrl = isMobileDevice()
        ? `${window.location.origin}/oauth-callback`
        : window.location.href;

      // Initiate Instagram auth with redirect URL
      const { authUrl } = await initiateInstagramAuth(selectedStylist, returnUrl);

      if (!authUrl) {
        throw new Error('No auth URL received from backend');
      }

      // Mobile browser: Use popup to prevent Instagram app intercept
      if (isMobileDevice()) {
        console.log('📱 Mobile browser detected - using popup approach');

        // CRITICAL: Open popup BEFORE any async operations (iOS requirement)
        const popup = openOAuthPopup(authUrl);

        // Check if popup was blocked
        if (!popup || popup.closed) {
          console.warn('⚠️ Popup was blocked by browser');
          setIsLoadingPosts(false);

          // Fallback: Navigate to intermediate page
          navigate('/oauth-redirect', {
            state: {
              authUrl: authUrl,
              returnPath: location.pathname
            }
          });
          return;
        }

        console.log('✅ OAuth popup opened successfully');

        // Monitor popup for completion
        monitorPopup(
          popup,
          (token) => {
            // Success: Token will be in URL, reload to process it
            console.log('✅ OAuth completed successfully');
            setIsLoadingPosts(false);
            // Reload to process the callback
            window.location.reload();
          },
          (error) => {
            // Error: Show message
            console.error('❌ OAuth popup error:', error);
            setIsLoadingPosts(false);
            toast.error(t('showcase.instagramAuthError', 'Failed to connect Instagram. Please try again.'));
          }
        );

      } else {
        // Desktop browser: Use traditional redirect (works perfectly)
        console.log('🖥️ Desktop browser detected - using traditional redirect');
        window.location.href = authUrl;
      }

    } catch (error) {
      console.error("Error with Instagram integration:", error);
      setIsLoadingPosts(false);
      toast.error(t('showcase.instagramAuthError', 'Failed to connect Instagram. Please try again.'));
    }
  };

  const handlePostSelection = (postId) => {
    setSelectedPosts(prev => {
      if (prev.includes(postId)) {
        return prev.filter(id => id !== postId);
      } else {
        return [...prev, postId];
      }
    });
  };

  const handleSyncSelectedPosts = async () => {
    if (!selectedStylist || selectedPosts.length === 0) return;

    setIsInstagramSyncing(true);
    try {
      // Get the selected posts with their updated captions
      const postsToSync = selectedPosts.map(postId => {
        const post = instagramPosts.find(p => p.id === postId);
        return {
          id: postId,
          caption: post.caption || ''
        };
      });

      await syncInstagramPosts(selectedStylist, postsToSync);
      toast.success(t('showcaseManagement.instagramSyncSuccess'));
      fetchShowcases();
      setShowInstagramPosts(false);
    } catch (error) {
      console.error("Error syncing selected Instagram posts:", error);

      // Check for duplicate Instagram post error (409 status or DUPLICATE_INSTAGRAM_POST code)
      if (error.response?.status === 409 ||
          error.response?.data?.code === 'DUPLICATE_INSTAGRAM_POST' ||
          error.response?.data?.message?.includes('already been uploaded')) {
        toast.error(t('showcaseManagement.duplicateInstagramPost'));
      } else {
        toast.error(error.response?.data?.message || t('showcaseManagement.instagramSyncError'));
      }
    } finally {
      setIsInstagramSyncing(false);
    }
  };

  const handleDeleteShowcase = (id) => {
    setShowcaseToDelete(id);
    setConfirmDelete(true);
  };

  const confirmDeleteShowcase = async () => {
    if (showcaseToDelete) {
      try {
        await deleteShowcase(showcaseToDelete);
        fetchShowcases();
      } catch (error) {
        console.error("Error deleting showcase:", error);
      } finally {
        setConfirmDelete(false);
        setShowcaseToDelete(null);
      }
    }
  };

  const handleOpenInstagramPosts = useCallback(async () => {
    if (!selectedStylist) return;

    setIsLoadingPosts(true);
    try {
      const { posts } = await getInstagramPosts(selectedStylist);
      setInstagramPosts(posts);
      setShowInstagramPosts(true);
    } catch (error) {
      console.error("Error fetching Instagram posts:", error);
    } finally {
      setIsLoadingPosts(false);
    }
  }, [selectedStylist]);

  const handleDisconnectInstagram = async () => {
    if (!selectedStylist) return;

    try {
      await disconnectInstagram(selectedStylist);
      setIsInstagramConnected(false);
      setConfirmDisconnect(false);
      setShowInstagramPosts(false); // Close the Instagram posts dialog
    } catch (error) {
      console.error("Error disconnecting Instagram:", error);
    }
  };

  const handleCommentUpdate = useCallback(async () => {
    if (selectedStylist) {
      setIsLoadingShowcases(true);
      try {
        const data = await getShowcases(selectedStylist);
        setShowcases(data);
        // Update the selected showcase with the latest data
        if (selectedShowcase) {
          const updatedShowcase = data.find(s => s._id === selectedShowcase._id);
          if (updatedShowcase) {
            setSelectedShowcase(updatedShowcase);
          }
        }
      } catch (error) {
        console.error("Error fetching showcases:", error);
      } finally {
        setIsLoadingShowcases(false);
      }
    }
  }, [selectedStylist, selectedShowcase]);

  // Check for Instagram authentication success in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const instagramSuccess = searchParams.get('instagram_success');

    if (instagramSuccess === 'true' && selectedStylist) {
      // Clear the URL parameters
      navigate(`/showcase-management/${selectedStylist}`, { replace: true });
      // Set Instagram as connected
      setIsInstagramConnected(true);
    }
  }, [location, selectedStylist, navigate]);

  // --- Presentation helpers ---
  const statusColorMap: Record<string, 'warning' | 'success' | 'error'> = { pending: 'warning', approved: 'success', rejected: 'error' };

  const createShowcaseButton = (variant: 'contained' | 'outlined' = 'contained') => (
    <Button
      variant={variant}
      startIcon={<AddPhotoAlternateIcon />}
      onClick={() => {
        setSelectedShowcase(null);
        setShowModal(true);
      }}
      sx={{ minHeight: 44 }}
    >
      {t('showcaseManagement.createShowcase')}
    </Button>
  );

  if (isLoadingStylists) {
    return (
      <PageContainer>
        <PageHeader
          title={t('showcaseManagement.title', 'Showcase Management')}
          subtitle={t('showcaseManagement.subtitle', 'Manage your portfolio and Instagram content.')}
          icon={<PhotoLibraryIcon />}
          classicPath="/classic/showcase-management"
        />
        <LoadingState variant="cards" />
      </PageContainer>
    );
  }

  if (!isLoadingStylists && stylists.length === 0) {
    return <NoStylist />;
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('showcaseManagement.title', 'Showcase Management')}
        subtitle={t('showcaseManagement.subtitle', 'Manage your portfolio and Instagram content.')}
        icon={<PhotoLibraryIcon />}
        classicPath="/classic/showcase-management"
        action={selectedStylist ? createShowcaseButton('contained') : undefined}
      />

      <Box sx={{ mb: 2.5 }}>
        <StylistSelector
          stylists={stylists}
          selectedStylistId={selectedStylist}
          onStylistSelect={handleStylistSelect}
          layout="wrap"
          showEmail={true}
          variant="id"
        />
      </Box>

      {selectedStylist && (
        <>
          {/* Instagram integration */}
          <Box sx={{ mb: 2.5 }}>
            <SectionCard title={t('showcaseManagement.instagram', 'Instagram')} icon={<InstagramIcon />}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {isInstagramConnected ? (
                    <Chip
                      icon={instagramTokenStatus?.isExpiringSoon ? <WarningIcon /> : <CheckCircleIcon />}
                      label={
                        instagramTokenStatus?.isExpiringSoon
                          ? t('showcaseManagement.instagramExpiring', {
                              days: instagramTokenStatus.expiresIn,
                              defaultValue: `Expires in ${instagramTokenStatus.expiresIn} days`
                            })
                          : t('showcaseManagement.instagramConnected')
                      }
                      color={instagramTokenStatus?.isExpiringSoon ? 'warning' : 'success'}
                      size="small"
                    />
                  ) : instagramTokenStatus?.isExpired ? (
                    <Chip
                      label={t('showcaseManagement.instagramExpired', { defaultValue: 'Expired (60 days)' })}
                      color="error"
                      size="small"
                    />
                  ) : (
                    <Chip
                      label={t('showcaseManagement.instagramNotConnected', 'Not connected')}
                      color="default"
                      size="small"
                    />
                  )}
                </Stack>

                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ '& > *': { minHeight: 44 } }}>
                  <Button
                    variant="outlined"
                    startIcon={<InstagramIcon />}
                    onClick={isInstagramConnected ? handleOpenInstagramPosts : handleInstagramAuth}
                    disabled={isInstagramSyncing || isCheckingToken}
                    color="primary"
                  >
                    {isInstagramConnected
                      ? t('showcaseManagement.viewInstagramPosts')
                      : t('showcaseManagement.connectInstagram')}
                  </Button>
                  {isInstagramConnected && (
                    <Button
                      variant="outlined"
                      startIcon={<DisconnectIcon />}
                      onClick={() => setConfirmDisconnect(true)}
                      color="error"
                    >
                      {t('showcaseManagement.disconnectInstagram')}
                    </Button>
                  )}
                </Stack>

                {hasInstagram && (
                  <>
                    <Divider />
                    <Box>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={autoContentSync?.enabled !== false}
                            onChange={handleToggleAutoSync}
                            disabled={isTogglingAutoSync}
                          />
                        }
                        label={t('showcaseManagement.autoSyncLabel', {
                          defaultValue: 'Automatic biweekly Instagram sync'
                        })}
                      />
                      <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {t('showcaseManagement.autoSyncDescription', {
                          defaultValue: 'Automatically pull new posts from Instagram every 2 weeks and add them as showcases.'
                        })}
                      </Typography>
                      {(autoContentSync?.lastRunAt || (autoContentSync?.nextRunAt && autoContentSync?.enabled !== false)) && (
                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 1 }}>
                          {autoContentSync?.lastRunAt && t('showcaseManagement.autoSyncLastRun', {
                            when: formatRelative(autoContentSync.lastRunAt),
                            defaultValue: `Last sync: ${formatRelative(autoContentSync.lastRunAt)}`
                          })}
                          {autoContentSync?.lastRunAt && autoContentSync?.nextRunAt && autoContentSync?.enabled !== false && ' • '}
                          {autoContentSync?.nextRunAt && autoContentSync?.enabled !== false && t('showcaseManagement.autoSyncNextRun', {
                            when: formatRelative(autoContentSync.nextRunAt),
                            defaultValue: `Next sync: ${formatRelative(autoContentSync.nextRunAt)}`
                          })}
                        </Typography>
                      )}
                      {autoContentSync?.lastRunStatus === 'failed' && autoContentSync?.lastRunError && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          {t('showcaseManagement.autoSyncLastError', {
                            error: autoContentSync.lastRunError,
                            defaultValue: `Last sync failed: ${autoContentSync.lastRunError}`
                          })}
                        </Alert>
                      )}
                    </Box>
                  </>
                )}
              </Stack>
            </SectionCard>
          </Box>

          <Dialog
            open={showModal}
            onClose={() => setShowModal(false)}
          >
            <DialogTitle>
              {selectedShowcase ? t('showcaseManagement.editShowcase') : t('showcaseManagement.createShowcase')}
            </DialogTitle>
            <DialogContent>
              <ShowcaseForm
                selectedStylist={selectedStylist}
                showcase={selectedShowcase}
                closeModal={() => setShowModal(false)}
              />
            </DialogContent>
          </Dialog>

          <Dialog
            open={confirmDelete}
            onClose={() => setConfirmDelete(false)}
          >
            <DialogTitle>{t('commonConfirmationDialog.confirmDeleteTitle')}</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                {t('commonConfirmationDialog.confirmDeleteMessage')}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={() => setConfirmDelete(false)}>
                  {t('commonConfirmationDialog.cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={confirmDeleteShowcase}
                >
                  {t('commonConfirmationDialog.confirm')}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          <Dialog
            open={confirmDisconnect}
            onClose={() => setConfirmDisconnect(false)}
          >
            <DialogTitle>{t('showcaseManagement.confirmDisconnectTitle')}</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                {t('showcaseManagement.confirmDisconnectMessage')}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={() => setConfirmDisconnect(false)}>
                  {t('commonConfirmationDialog.cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDisconnectInstagram}
                >
                  {t('showcaseManagement.disconnect')}
                </Button>
              </Box>
            </DialogContent>
          </Dialog>

          <InstagramPostsDialog
            open={showInstagramPosts}
            onClose={() => setShowInstagramPosts(false)}
            posts={instagramPosts}
            selectedPosts={selectedPosts}
            onPostSelection={handlePostSelection}
            onSync={handleSyncSelectedPosts}
            isLoading={isLoadingPosts}
            isSyncing={isInstagramSyncing}
            onCaptionChange={handleCaptionChange}
            onDisconnect={() => setConfirmDisconnect(true)}
          />

          <CommentsDialog
            open={showComments}
            onClose={() => {
              setShowComments(false);
              setSelectedShowcase(null);
            }}
            showcase={selectedShowcase}
            onCommentUpdate={handleCommentUpdate}
          />

          {/* Showcase grid */}
          {isLoadingShowcases ? (
            <LoadingState variant="cards" />
          ) : showcases.length === 0 ? (
            <SectionCard>
              <EmptyState
                icon={<PhotoLibraryIcon />}
                title={t('showcaseManagement.emptyTitle', 'No showcases yet')}
                description={t(
                  'showcaseManagement.emptyDescription',
                  'Create your first showcase, or connect Instagram to sync your posts as showcases.'
                )}
                action={createShowcaseButton('contained')}
              />
            </SectionCard>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              }}
            >
              {showcases.map((showcase) => {
                const media = showcase.media?.[0];
                const mediaSrc = media ? `${import.meta.env.VITE_R2_ENDPOINT}/${media.key}` : null;
                const isActive = showcase.status === 'approved';
                return (
                  <Card
                    key={showcase._id}
                    sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
                  >
                    {/* Media on top, 4:3 aspect */}
                    <Box sx={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', bgcolor: brand.creamDark }}>
                      {media ? (
                        media.mediaType === 'image' ? (
                          <CardMedia
                            component="img"
                            image={mediaSrc}
                            alt={t('showcaseManagement.showcaseMedia', 'Showcase Media')}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <CardMedia
                            component="video"
                            src={mediaSrc}
                            controls
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        )
                      ) : (
                        <Skeleton variant="rectangular" width="100%" height="100%" animation={false} />
                      )}
                    </Box>

                    <CardContent sx={{ flexGrow: 1, p: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        <Chip
                          size="small"
                          label={t(`showcaseManagement.${showcase.status}`)}
                          color={isActive ? 'success' : (statusColorMap[showcase.status] || 'default')}
                          variant={isActive ? 'filled' : 'outlined'}
                        />
                        {showcase.postType === 'brochure' && (
                          <Chip
                            size="small"
                            label={t('showcaseManagement.brochure')}
                            color="warning"
                            variant="outlined"
                          />
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {new Date(showcase.createdAt).toLocaleDateString()}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1.25,
                          minHeight: '2.5em',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {showcase.content}
                      </Typography>

                      {showcase.status === 'rejected' && showcase.rejectionReason && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                          {t('showcaseManagement.reason')}: {showcase.rejectionReason}
                        </Typography>
                      )}

                      {/* Stats row */}
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          p: 1,
                          borderRadius: 2,
                          bgcolor: brand.cream,
                          border: `1px solid ${brand.border}`,
                        }}
                      >
                        {[
                          { label: t('showcaseManagement.likes'), value: showcase.likes.length },
                          { label: t('showcaseManagement.comments'), value: showcase.comments.length },
                          { label: t('showcaseManagement.shares'), value: showcase.shares.length },
                          { label: t('showcaseManagement.clicks'), value: showcase.clicks.length },
                        ].map((stat) => (
                          <Box key={stat.label} sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {stat.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {stat.label}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>

                    <Divider />
                    <CardActions sx={{ p: 1.5, gap: 1, flexWrap: 'wrap', '& > *': { minHeight: 40 } }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          setSelectedShowcase(showcase);
                          setShowModal(true);
                        }}
                      >
                        {t('showcaseManagement.edit')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CommentIcon />}
                        onClick={() => {
                          setSelectedShowcase(showcase);
                          setShowComments(true);
                        }}
                      >
                        {t('showcaseManagement.manageComments')}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => handleDeleteShowcase(showcase._id)}
                        sx={{ ml: 'auto' }}
                      >
                        {t('showcaseManagement.delete')}
                      </Button>
                    </CardActions>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )}
    </PageContainer>
  );
};

const WrappedShowcaseManagementPage = withUser(ShowcaseManagementPage);
export default WrappedShowcaseManagementPage;
